import React, { useState } from "react";
import { mdiTrayArrowDown } from "@mdi/js";
import Icon from "@mdi/react";
import clsx from "clsx";
import { useToggle } from "usehooks-ts";

import Button from "components/Button";
import Header from "components/Header";
import Modal from "components/Modal";
import StatusModal from "components/StatusModal";
import { ExclamationSVGIcon } from "components/SVGIcons";
import { useUserFeature } from "hooks/useUserFeature";
import MainLayout from "layouts/Main";
import { uploadFRDocsAPI } from "services/api/haulpay";
import FileUploader from "./FileUploader";
import FileList from "./FileList";
import styles from "./style.module.scss";

export default function BulkUploads() {
  const { isCarrierSubscribed } = useUserFeature();
  const [isPermissionModalOpen, togglePermissionModal] = useToggle();
  const [isSuccessModalOpen, toggleSuccessModal] = useToggle();
  const [isErrorModalOpen, toggleErrorModal] = useToggle();
  const [isLoading, setIsLoading] = useState(false);
  const [loadMessage, setLoadMessage] = useState<string[]>([]);
  const [fileList, setFileList] = useState<File[]>([]);

  const addFile = (file: File) => {
    setFileList((prev) => [...prev, file]);
  };

  const removeFile = (fileId: number) => {
    setFileList((prev) => prev.filter((file) => file.lastModified !== fileId));
  };

  const processMessages = (items: string[], isCarrierSubscribed: boolean) => {
    const loadType = isCarrierSubscribed ? "Load Number" : "Load Id";
    return items.map((item) => `${loadType}: ${item[0]}, ${item[1]}`);
  };

  const bulkFundingRequest = async () => {
    try {
      setIsLoading(true);
      setLoadMessage([]);
      const response = await uploadFRDocsAPI(fileList[0]);
      const failMessages = processMessages(response.data.fail as string[], isCarrierSubscribed);
      const successMessages = processMessages(
        response.data.success as string[],
        isCarrierSubscribed
      );
      setLoadMessage([...failMessages, ...successMessages]);

      if ((response.data.fail as string[]).length > 0) {
        toggleErrorModal();
      } else {
        toggleSuccessModal();
      }
    } catch {
      togglePermissionModal();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <MainLayout
      desktopTitle="Bulk Upload"
      customHeader={<Header label="Bulk Upload" isHideDesktop />}
      className={clsx(styles.bulkUploadsContainer, "desktop:!mx-auto")}>
      <div className={styles.wrapper}>
        <div className={styles.subtitle}>
          <span>File Name</span>
          <span>Action</span>
        </div>
        <div className={styles.content}>
          <FileList files={fileList} onRemove={removeFile} />
          {!isCarrierSubscribed && <span className={styles.loadLabel}>Load</span>}
          <FileUploader isCarrierSubscribed={isCarrierSubscribed} onFileSelect={addFile} />
        </div>
      </div>

      <div className={styles.footer}>
        {isCarrierSubscribed ? (
          <a
            href="/other/template_carrier_bulk_import_example.xlsx"
            target="_blank"
            rel="noopener noreferrer"
            download="template_carrier_bulk_import_example.xlsx"
            className={clsx(styles.csvTemplate, "desktop:!w-48")}>
            <Icon path={mdiTrayArrowDown} />
            <span>CSV Template</span>
          </a>
        ) : (
          <a
            href="/other/broker_bulk_upload_template.xlsx"
            target="_blank"
            rel="noopener noreferrer"
            download="broker_bulk_upload_template.xlsx"
            className={clsx(styles.csvTemplate, "desktop:!w-48")}>
            <Icon path={mdiTrayArrowDown} />
            <span>CSV Template</span>
          </a>
        )}
        <Button
          type="button"
          size="lg"
          className="desktop:!w-48 disabled:bg-subtitle"
          onClick={bulkFundingRequest}
          loading={isLoading}>
          Next
        </Button>
      </div>

      <Modal
        isOpen={isPermissionModalOpen}
        closeModal={togglePermissionModal}
        wrapperClass="!my-auto text-sm"
        dialogClass="desktop:!m-auto !bg-foreground !px-6 !pt-5 !pb-3 !border !border-card !overflow-y-auto gap-4"
        closeClass="hidden">
        <ExclamationSVGIcon className="self-center" />
        <span className="pt-1 font-medium text-center">
          You do not have permission to perform this action.
        </span>
        <Button onClick={togglePermissionModal} className="self-center w-20 rounded-full h-9">
          OK
        </Button>
      </Modal>

      <StatusModal
        title="Success"
        content={loadMessage.map((item) => (
          <p key={item} className="pb-2 !text-sm desktop:!text-base">
            {item}
          </p>
        ))}
        isModalOpen={isSuccessModalOpen}
        toggleModal={toggleSuccessModal}
        backdropClick={toggleSuccessModal}
      />

      <StatusModal
        title="Error"
        content={loadMessage.map((item) => (
          <p key={item} className="pb-2 !text-sm desktop:!text-base">
            {item}
          </p>
        ))}
        isModalOpen={isErrorModalOpen}
        toggleModal={toggleErrorModal}
        backdropClick={toggleErrorModal}
        isError
      />
    </MainLayout>
  );
}
